import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { useAuthState } from '../App';

interface Team {
  id: string;
  name: string;
  plan: string;
  created_at: string;
}

interface TeamsResponse {
  teams: Team[];
}

export interface Project {
  id: string;
  team_id: string;
  name: string;
  slug: string;
  description?: string;
  created_at: string;
}

export interface Environment {
  id: string;
  name: string;
  project_id: string;
  inherits_from?: string;
  created_at: string;
}

interface ProjectsResponse {
  projects: Project[];
}

interface EnvironmentsResponse {
  environments: Environment[];
}

interface ProjectsContextType {
  // Team (for limits and billing)
  primaryTeamId: string | null;
  primaryTeam: Team | undefined;

  // Projects
  projects: Project[];
  isLoadingProjects: boolean;
  projectsError: Error | null;
  refetchProjects: () => void;

  // Selected project
  selectedProject: Project | undefined;
  selectedProjectId: string | null;
  selectProject: (projectId: string | null) => void;

  // Create project
  createProject: (data: { name: string; description?: string }, options?: { onSuccess?: (project: Project) => void }) => void;
  isCreatingProject: boolean;
  createProjectError: Error | null;

  // Update project
  updateProject: (args: { projectId: string; data: { name?: string; description?: string } }) => void;
  isUpdatingProject: boolean;
  updateProjectError: Error | null;

  // Delete project
  deleteProject: (projectId: string) => void;
  isDeletingProject: boolean;
  deleteProjectError: Error | null;

  // Environments
  environments: Environment[];
  isLoadingEnvironments: boolean;
  environmentsError: Error | null;
  refetchEnvironments: () => void;

  // Create environment
  createEnvironment: (data: { name: string; inherits_from?: string }) => Promise<Environment>;
  isCreatingEnvironment: boolean;
  createEnvironmentError: Error | null;
}

const ProjectsContext = createContext<ProjectsContextType | null>(null);

export function ProjectsProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuthState();
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    () => {
      if (typeof window !== 'undefined') {
        return localStorage.getItem('selectedProjectId');
      }
      return null;
    }
  );

  // Fetch user's teams (to get team_id for creating projects)
  const teamsQuery = useQuery({
    queryKey: ['teams'],
    queryFn: async () => {
      const response = await apiClient.get<TeamsResponse>('/api/v1/teams');
      return response.data.teams;
    },
    staleTime: 1000 * 60 * 5,
    enabled: isAuthenticated,
  });

  // Get the primary team (first team - usually the personal team)
  const primaryTeamId = teamsQuery.data?.[0]?.id;

  // Fetch all projects (only when authenticated)
  const projectsQuery = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const response = await apiClient.get<ProjectsResponse>('/api/v1/projects');
      return response.data.projects;
    },
    staleTime: 1000 * 60 * 5,
    enabled: isAuthenticated,
  });

  // Get the selected project
  const selectedProject = projectsQuery.data?.find(
    (p) => p.id === selectedProjectId
  );

  // Select a project and persist to localStorage
  const selectProject = useCallback((projectId: string | null) => {
    setSelectedProjectId(projectId);
    if (typeof window !== 'undefined') {
      if (projectId) {
        localStorage.setItem('selectedProjectId', projectId);
      } else {
        localStorage.removeItem('selectedProjectId');
      }
    }
  }, []);

  // Create a new project
  const createProjectMutation = useMutation({
    mutationFn: async (data: { name: string; description?: string }) => {
      if (!primaryTeamId) {
        throw new Error('No team available. Please refresh the page.');
      }
      const response = await apiClient.post<Project>('/api/v1/projects', {
        ...data,
        team_id: primaryTeamId,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  // Update a project
  const updateProjectMutation = useMutation({
    mutationFn: async ({
      projectId,
      data,
    }: {
      projectId: string;
      data: { name?: string; description?: string };
    }) => {
      const response = await apiClient.patch<Project>(
        `/api/v1/projects/${projectId}`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  // Delete a project
  const deleteProjectMutation = useMutation({
    mutationFn: async (projectId: string) => {
      await apiClient.delete(`/api/v1/projects/${projectId}`);
    },
    onSuccess: (_, deletedProjectId) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      if (selectedProjectId === deletedProjectId) {
        selectProject(null);
      }
    },
  });

  const environmentsQuery = useQuery({
    queryKey: ['environments', selectedProjectId],
    queryFn: async () => {
      if (!selectedProjectId) return [];
      const response = await apiClient.get<EnvironmentsResponse>(
        `/api/v1/projects/${selectedProjectId}/environments`
      );
      return response.data.environments;
    },
    enabled: !!selectedProjectId && isAuthenticated,
    staleTime: 1000 * 60 * 5,
  });

  const createEnvironmentMutation = useMutation({
    mutationFn: async (data: { name: string; inherits_from?: string }) => {
      if (!selectedProjectId) throw new Error('No project selected');
      const response = await apiClient.post<Environment>(
        `/api/v1/projects/${selectedProjectId}/environments`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['environments', selectedProjectId] });
    },
  });

  const value: ProjectsContextType = {
    // Team (for limits and billing)
    primaryTeamId: primaryTeamId ?? null,
    primaryTeam: teamsQuery.data?.[0],

    // Projects
    projects: projectsQuery.data ?? [],
    isLoadingProjects: projectsQuery.isLoading,
    projectsError: projectsQuery.error,
    refetchProjects: projectsQuery.refetch,

    // Selected project
    selectedProject,
    selectedProjectId,
    selectProject,

    // Create project
    createProject: createProjectMutation.mutate,
    isCreatingProject: createProjectMutation.isPending,
    createProjectError: createProjectMutation.error,

    // Update project
    updateProject: updateProjectMutation.mutate,
    isUpdatingProject: updateProjectMutation.isPending,
    updateProjectError: updateProjectMutation.error,

    // Delete project
    deleteProject: deleteProjectMutation.mutate,
    isDeletingProject: deleteProjectMutation.isPending,
    deleteProjectError: deleteProjectMutation.error,

    // Environments
    environments: environmentsQuery.data ?? [],
    isLoadingEnvironments: environmentsQuery.isLoading,
    environmentsError: environmentsQuery.error,
    refetchEnvironments: environmentsQuery.refetch,

    // Create environment
    createEnvironment: createEnvironmentMutation.mutateAsync,
    isCreatingEnvironment: createEnvironmentMutation.isPending,
    createEnvironmentError: createEnvironmentMutation.error,
  };

  return (
    <ProjectsContext.Provider value={value}>
      {children}
    </ProjectsContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useProjects(): ProjectsContextType {
  const context = useContext(ProjectsContext);
  if (!context) {
    throw new Error('useProjects must be used within a ProjectsProvider');
  }
  return context;
}
